// The viewer (§10): WebGL canvas + a 2D overlay canvas on top (crop, thirds grid, straighten line,
// split line). Zoom to fit by default, 100% with Z, wheel zooms around the cursor (fit ↔ 100%),
// space-drag pans at 100%.
import { useEffect, useRef, useState } from 'react';
import { PreviewController, type Layout } from '../../engine/preview';
import { useUi } from '../../state/ui';
import { useEditor } from '../../state/editor';
import { useLibrary } from '../../state/library';
import { decodePool } from '../../io/decode/pool';
import { canSolveWb, solveWb } from '../../color/wb';
import { errorKey } from '../../i18n/errors';
import { straightenAngle, type Rect } from '../../engine/geometry';
import { reframe } from '../panels/GeometryPanel';
import { defaultSettings } from '../../model/defaults';
import type { Histogram } from '../../engine/histogram';
import type { DecodedImage } from '../../io/decode/types';
import { useT } from '../../i18n';
import { matteFor, matteInMemory, useMatte } from '../../features/background/matte';
import { getLut } from '../../features/lut/store';
import { grainSeed } from '../../engine/grain';
import { upscaledBlob, upscaleKey, useUpscale } from '../../features/upscale/upscale';
import {
  drawLocalOverlay,
  isLocalTool,
  localDown,
  localMove,
  localUp,
  overlayFor,
  type LocalDrag,
} from './localInput';

type Drag =
  | { kind: 'pan'; x: number; y: number; pan: { x: number; y: number } }
  | {
      kind: 'crop';
      mode: 'move' | 'nw' | 'ne' | 'sw' | 'se' | 'rotate';
      x: number;
      y: number;
      crop: Rect;
      angle: number;
    }
  | { kind: 'straighten'; x0: number; y0: number; x1: number; y1: number }
  | { kind: 'split' }
  | { kind: 'local'; d: LocalDrag };

const DEFAULTS = defaultSettings();

export const viewerHooks: { controller: PreviewController | null; decoded: DecodedImage | null } = {
  controller: null,
  decoded: null,
};

export function Viewer({ onHistogram }: { onHistogram: (h: Histogram | null) => void }) {
  const t = useT();
  const wrap = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const overlay = useRef<HTMLCanvasElement>(null);
  const ctrl = useRef<PreviewController | null>(null);
  const [layout, setLayout] = useState<Layout | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const drag = useRef<Drag | null>(null);
  const space = useRef(false);
  const hover = useRef<{ x: number; y: number } | null>(null);
  const [, force] = useState(0);

  const activeId = useLibrary((s) => s.activeId);
  const photo = useLibrary((s) => s.photos.find((p) => p.id === s.activeId) ?? null);
  // A photo that was never edited has no entry yet: fall back to the defaults (stable object).
  const settings = useEditor((s) => (activeId ? s.settings[activeId] : undefined)) ?? DEFAULTS;
  const ui = useUi();

  // Controller lifecycle.
  useEffect(() => {
    const c = new PreviewController(canvas.current!, onHistogram, (l) => setLayout(l));
    ctrl.current = c;
    viewerHooks.controller = c;
    const ro = new ResizeObserver(() => {
      const r = wrap.current!.getBoundingClientRect();
      c.resize(r.width, r.height, window.devicePixelRatio || 1);
      const o = overlay.current!;
      o.width = Math.round(r.width * (window.devicePixelRatio || 1));
      o.height = Math.round(r.height * (window.devicePixelRatio || 1));
      force((n) => n + 1);
    });
    ro.observe(wrap.current!);
    return () => {
      ro.disconnect();
      c.dispose();
      ctrl.current = null;
      viewerHooks.controller = null;
    };
  }, [onHistogram]);

  // Decode the active photo — or its upscaled source (M13) when settings.upscale is on.
  const upKey = photo && settings.upscale.factor > 1 ? upscaleKey(photo, settings.upscale) : '';
  const upVersion = useUpscale((u) => u.version);
  useEffect(() => {
    let alive = true;
    const c = ctrl.current;
    if (!c) return;
    if (!photo) {
      c.setImage(null);
      onHistogram(null);
      return;
    }
    setStatus(t('decoding'));
    const s = useEditor.getState().settings[photo.id];
    upscaledBlob(photo, s)
      .catch(() => null) // the Upscale panel shows the error; the viewer falls back to the original
      .then(async (up) => ({ up, img: await decodePool().decode(up ?? photo.file) }))
      .then(({ up, img }) => {
        if (!alive) return;
        viewerHooks.decoded = img;
        c.setGrainSeed(grainSeed(photo.name, photo.size, photo.lastModified));
        c.setImage(img);
        // photo.width/height always hold the ORIGINAL's size (M13 relies on it).
        if (!up && (photo.width !== img.width || photo.height !== img.height))
          useLibrary
            .getState()
            .patch(photo.id, { width: img.width, height: img.height, orientation: img.source.orientation });
        setStatus(null);
      })
      .catch((e: Error) => {
        console.warn('decode failed', photo.name, e);
        if (alive) setStatus(t('decodeFailed', { name: photo.name, reason: t(errorKey(e)) }));
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photo?.id, upKey, upVersion]);

  // Push state to the renderer.
  useEffect(() => {
    ctrl.current?.setState({
      settings,
      zoom: ui.zoom,
      pan: ui.pan,
      before: ui.before,
      split: ui.split,
      splitPos: ui.splitPos,
      clipping: ui.clipping,
      dragging: ui.dragging,
      showFrame: ui.tool === 'crop',
      overlay: overlayFor(settings, ui),
      bgView: ui.bgView,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    settings,
    ui.zoom,
    ui.pan,
    ui.before,
    ui.split,
    ui.splitPos,
    ui.clipping,
    ui.dragging,
    ui.tool,
    ui.maskId,
    ui.maskOverlay,
    ui.bgView,
  ]);

  // LUT (§11.6): load the referenced `.cube` from IndexedDB into the renderer.
  const lutId = settings.lut.id;
  const hasLayout = layout !== null;
  useEffect(() => {
    let alive = true;
    if (!lutId) ctrl.current?.setLut(null);
    else void getLut(lutId).then((l) => alive && ctrl.current?.setLut(l, lutId));
    return () => {
      alive = false;
    };
  }, [lutId, hasLayout]);

  // Background matte (§11.12): from memory at once, else computed/loaded asynchronously.
  const matteVersion = useMatte((m) => m.version);
  const bg = settings.background;
  useEffect(() => {
    const c = ctrl.current;
    if (!c || !photo) return;
    if (bg.mode === 'off') {
      c.setMatte(null);
      return;
    }
    const hit = matteInMemory(photo, settings);
    if (hit) {
      c.setMatte(hit.matte, hit.key);
      return;
    }
    let alive = true;
    matteFor(photo, settings)
      .then((r) => alive && r && ctrl.current?.setMatte(r.matte, r.key))
      .catch(() => alive && ctrl.current?.setMatte(null));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photo?.id, bg.mode, JSON.stringify(bg.key), bg.maskCacheKey, matteVersion, layout !== null]);

  // Overlay drawing.
  useEffect(() => {
    const o = overlay.current;
    if (!o) return;
    const dpr = window.devicePixelRatio || 1;
    const ctx = o.getContext('2d')!;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, o.width, o.height);
    if (!layout || !settings) return;
    const r = layout.rect;
    if (ui.tool === 'crop') {
      const c = settings.geometry.crop;
      const x = r.x + c.x * r.w,
        y = r.y + c.y * r.h,
        w = c.w * r.w,
        h = c.h * r.h;
      ctx.fillStyle = 'rgba(0,0,0,0.5)';
      ctx.beginPath();
      ctx.rect(r.x, r.y, r.w, r.h);
      ctx.rect(x, y, w, h);
      ctx.fill('evenodd');
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1;
      ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
      if (drag.current?.kind === 'crop') {
        ctx.strokeStyle = 'rgba(255,255,255,0.6)';
        ctx.beginPath();
        for (let i = 1; i < 3; i++) {
          ctx.moveTo(x + (w * i) / 3, y);
          ctx.lineTo(x + (w * i) / 3, y + h);
          ctx.moveTo(x, y + (h * i) / 3);
          ctx.lineTo(x + w, y + (h * i) / 3);
        }
        ctx.stroke();
      }
      ctx.fillStyle = '#fff';
      for (const [hx, hy] of [
        [x, y],
        [x + w, y],
        [x, y + h],
        [x + w, y + h],
      ] as const)
        ctx.fillRect(hx - 5, hy - 5, 10, 10);
    }
    if (drag.current?.kind === 'straighten') {
      const d = drag.current;
      ctx.strokeStyle = '#ffd400';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(d.x0, d.y0);
      ctx.lineTo(d.x1, d.y1);
      ctx.stroke();
    }
    if (isLocalTool(ui.tool) && ctrl.current)
      drawLocalOverlay(
        ctx,
        { ctrl: ctrl.current, settings, ui },
        hover.current,
        drag.current?.kind === 'local' ? drag.current.d : null,
      );
    if (ui.split && !ui.before) {
      const sx = r.x + r.w * ui.splitPos;
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(sx, r.y);
      ctx.lineTo(sx, r.y + r.h);
      ctx.stroke();
      ctx.font = '12px system-ui';
      ctx.fillStyle = '#fff';
      ctx.fillText(t('before'), r.x + 6, r.y + 16);
      ctx.fillText(t('after'), sx + 6, r.y + 16);
    }
  });

  // Space key for panning.
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !(e.target instanceof HTMLInputElement)) space.current = true;
    };
    const up = (e: KeyboardEvent) => {
      if (e.code === 'Space') space.current = false;
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, []);

  const local = (e: React.PointerEvent | React.WheelEvent) => {
    const b = wrap.current!.getBoundingClientRect();
    return { x: e.clientX - b.left, y: e.clientY - b.top };
  };

  const onPointerDown = (e: React.PointerEvent) => {
    const c = ctrl.current;
    if (!c || !layout || !activeId || !settings) return;
    const p = local(e);
    (e.target as Element).setPointerCapture(e.pointerId);
    const r = layout.rect;
    if (ui.split && !ui.before && Math.abs(p.x - (r.x + r.w * ui.splitPos)) < 8) {
      drag.current = { kind: 'split' };
      return;
    }
    if (ui.tool === 'wbPicker') {
      const rgb = c.sampleForWb(p.x, p.y);
      // A click on black changes nothing (S12); the tool stays on for another try.
      if (rgb && canSolveWb(rgb)) {
        const { temp, tint } = solveWb(rgb);
        useEditor.getState().update(activeId, (s) => {
          s.wb.temp = Math.round(temp);
          s.wb.tint = Math.round(tint);
        });
        useUi.getState().set({ tool: 'none' });
      }
      return;
    }
    if (ui.tool === 'straighten') {
      drag.current = { kind: 'straighten', x0: p.x, y0: p.y, x1: p.x, y1: p.y };
      return;
    }
    if (ui.tool === 'bgKey') {
      // Backdrop colour for key mode: the source colour (linear, before any edit), 5×5 average.
      const uv = c.viewToSource(p.x, p.y);
      const rgb = uv && c.engine.readPreviewBlock(uv[0], uv[1], 2);
      if (rgb) {
        useEditor.getState().update(activeId, (s) => {
          s.background.mode = 'key';
          s.background.key = {
            tolerance: 20,
            softness: 20,
            connected: true,
            ...s.background.key,
            color: rgb,
          };
        });
        useUi.getState().set({ tool: 'none' });
      }
      return;
    }
    if (isLocalTool(ui.tool) && !space.current) {
      const d = localDown({ ctrl: c, id: activeId, settings, ui }, p.x, p.y, e);
      if (d) drag.current = { kind: 'local', d };
      force((n) => n + 1);
      return;
    }
    if (ui.tool === 'crop') {
      const cr = settings.geometry.crop;
      const x = r.x + cr.x * r.w,
        y = r.y + cr.y * r.h,
        w = cr.w * r.w,
        h = cr.h * r.h;
      const near = (ax: number, ay: number) => Math.abs(p.x - ax) < 10 && Math.abs(p.y - ay) < 10;
      const mode = near(x, y)
        ? 'nw'
        : near(x + w, y)
          ? 'ne'
          : near(x, y + h)
            ? 'sw'
            : near(x + w, y + h)
              ? 'se'
              : p.x > x && p.x < x + w && p.y > y && p.y < y + h
                ? 'move'
                : 'rotate';
      drag.current = { kind: 'crop', mode, x: p.x, y: p.y, crop: { ...cr }, angle: settings.geometry.angle };
      return;
    }
    if (ui.zoom === 1 || space.current) drag.current = { kind: 'pan', x: p.x, y: p.y, pan: { ...ui.pan } };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (isLocalTool(ui.tool)) {
      hover.current = local(e);
      if (!d) force((n) => n + 1);
    }
    if (!d || !layout || !activeId || !settings || !photo) return;
    const p = local(e);
    if (d.kind === 'local') {
      if (ctrl.current && localMove({ ctrl: ctrl.current, id: activeId, settings, ui }, d.d, p.x, p.y, e))
        force((n) => n + 1);
      return;
    }
    const r = layout.rect;
    if (d.kind === 'split') {
      useUi.getState().set({ splitPos: Math.min(1, Math.max(0, (p.x - r.x) / r.w)) });
    } else if (d.kind === 'pan') {
      const k = layout.dpr;
      useUi.getState().set({ pan: { x: d.pan.x - (p.x - d.x) * k, y: d.pan.y - (p.y - d.y) * k } });
    } else if (d.kind === 'straighten') {
      d.x1 = p.x;
      d.y1 = p.y;
      force((n) => n + 1);
    } else if (d.kind === 'crop') {
      const W = photo.width || 1,
        H = photo.height || 1;
      const dx = (p.x - d.x) / r.w,
        dy = (p.y - d.y) / r.h;
      useEditor.getState().update(
        activeId,
        (s) => {
          const g = s.geometry;
          if (d.mode === 'rotate') {
            const cx = r.x + (d.crop.x + d.crop.w / 2) * r.w,
              cy = r.y + (d.crop.y + d.crop.h / 2) * r.h;
            const a0 = Math.atan2(d.y - cy, d.x - cx),
              a1 = Math.atan2(p.y - cy, p.x - cx);
            g.angle = Math.max(-45, Math.min(45, d.angle + ((a1 - a0) * 180) / Math.PI));
            reframe(s, W, H, { refit: true });
            return;
          }
          let { x, y, w, h } = d.crop;
          if (d.mode === 'move') {
            x = Math.min(1 - w, Math.max(0, x + dx));
            y = Math.min(1 - h, Math.max(0, y + dy));
          } else {
            const aspect =
              g.aspect === 'original'
                ? W / H
                : g.aspect && g.aspect !== 'free'
                  ? (
                      {
                        '1:1': 1,
                        '4:5': 0.8,
                        '3:2': 1.5,
                        '2:3': 2 / 3,
                        '16:9': 16 / 9,
                        '9:16': 9 / 16,
                        '4:3': 4 / 3,
                      } as Record<string, number>
                    )[g.aspect]
                  : null;
            const left = d.mode === 'nw' || d.mode === 'sw',
              top = d.mode === 'nw' || d.mode === 'ne';
            let nx0 = left ? x + dx : x,
              nx1 = left ? x + w : x + w + dx;
            let ny0 = top ? y + dy : y,
              ny1 = top ? y + h : y + h + dy;
            nx0 = Math.max(0, nx0);
            ny0 = Math.max(0, ny0);
            nx1 = Math.min(1, nx1);
            ny1 = Math.min(1, ny1);
            let nw = Math.max(0.02, nx1 - nx0),
              nh = Math.max(0.02, ny1 - ny0);
            if (aspect) {
              // Keep pixel aspect: w·W / (h·H) = aspect, driven by the larger change.
              const wFromH = (nh * H * aspect) / W;
              if (wFromH > nw) nh = (nw * W) / (H * aspect);
              else nw = wFromH;
              if (left) nx0 = nx1 - nw;
              if (top) ny0 = ny1 - nh;
            }
            x = nx0;
            y = ny0;
            w = nw;
            h = nh;
          }
          g.crop = { x, y, w, h };
          if (g.constrain) reframe(s, W, H, { refit: false });
        },
        { commit: false },
      );
    }
  };

  const onPointerUp = () => {
    const d = drag.current;
    drag.current = null;
    if (!d || !activeId || !settings || !photo || !layout) return;
    if (d.kind === 'local' && ctrl.current) localUp({ ctrl: ctrl.current, id: activeId, settings, ui }, d.d);
    if (d.kind === 'crop') useEditor.getState().commit(activeId);
    if (d.kind === 'straighten') {
      const r = layout.rect;
      const W = photo.width || 1,
        H = photo.height || 1;
      // Overlay pixels → frame pixels (in crop mode the view shows the frame; otherwise the crop).
      const g = settings.geometry;
      const fx = (px: number) =>
        (ui.tool === 'crop' ? (px - r.x) / r.w : g.crop.x + ((px - r.x) / r.w) * g.crop.w) * W;
      const fy = (py: number) =>
        (ui.tool === 'crop' ? (py - r.y) / r.h : g.crop.y + ((py - r.y) / r.h) * g.crop.h) * H;
      if (Math.hypot(d.x1 - d.x0, d.y1 - d.y0) > 10) {
        const a = straightenAngle(g.angle, fx(d.x0), fy(d.y0), fx(d.x1), fy(d.y1));
        useEditor.getState().update(activeId, (s) => {
          s.geometry.angle = a;
          reframe(s, W, H, { refit: true });
        });
      }
      useUi.getState().set({ tool: 'none' });
    }
    force((n) => n + 1);
  };

  const onWheel = (e: React.WheelEvent) => {
    const c = ctrl.current;
    if (!c || !layout) return;
    const p = local(e);
    if (e.deltaY < 0 && ui.zoom === 'fit') {
      // Zoom to 100% around the cursor: keep the image point under the cursor fixed.
      const fx = (p.x - layout.rect.x) / layout.rect.w,
        fy = (p.y - layout.rect.y) / layout.rect.h;
      const vw = (wrap.current!.clientWidth || 1) * layout.dpr,
        vh = (wrap.current!.clientHeight || 1) * layout.dpr;
      useUi.getState().set({
        zoom: 1,
        pan: {
          x: fx * layout.full.width - (p.x / (wrap.current!.clientWidth || 1)) * vw,
          y: fy * layout.full.height - (p.y / (wrap.current!.clientHeight || 1)) * vh,
        },
      });
    } else if (e.deltaY > 0 && ui.zoom === 1) useUi.getState().set({ zoom: 'fit' });
  };

  const cursor =
    ui.tool === 'wbPicker' || ui.tool === 'straighten' || ui.tool === 'bgKey' || isLocalTool(ui.tool)
      ? 'crosshair'
      : ui.zoom === 1
        ? 'grab'
        : 'default';
  return (
    <div ref={wrap} style={{ position: 'absolute', inset: 0 }} data-testid="viewer">
      <canvas ref={canvas} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
      <canvas
        ref={overlay}
        data-testid="viewer-overlay"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', cursor, touchAction: 'none' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={() => {
          hover.current = null;
          force((n) => n + 1);
        }}
        onWheel={onWheel}
      />
      {status && (
        <div style={{ position: 'absolute', left: 12, bottom: 12, color: 'var(--muted)' }}>{status}</div>
      )}
      {!photo && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            color: 'var(--muted)',
            pointerEvents: 'none',
          }}
        >
          {t('viewerEmpty')}
        </div>
      )}
    </div>
  );
}
